import React, { useState, useRef, useEffect } from 'react';
import { Settings } from "lucide-react";
import { Tooltip } from 'components/atoms/Base/Logo/Tooltip';
import { UserData } from 'common/models/navigation';
import { getUserInitials } from 'common/helpers/auth-helper';
import { SignOutButton } from 'components/atoms/Custom/SignOutButton/SignOutButton';

interface UserProfileProps {
  user: UserData;
  collapsed?: boolean;
  onSettings?: () => void;
  isLoading?: boolean;
}

export const UserProfile: React.FC<UserProfileProps> = ({ 
  user, 
  collapsed = false,
  onSettings,
  isLoading = false
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const initials = getUserInitials(user.name);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  if (collapsed) {
    return (
      <div className={`mt-auto ${collapsed ? 'p-3' : 'p-2'} relative`} ref={dropdownRef}>
        <Tooltip text={user.name} position="right">
          <div 
            className="w-6 h-6 mx-auto bg-primary rounded-full flex items-center justify-center transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] shadow-sm cursor-pointer hover:bg-blue-600"
            onClick={toggleDropdown}
          >
            <span className="text-white text-ui ">{initials}</span>
          </div>
        </Tooltip>

        {/* Dropdown menu for collapsed state */}
        {isDropdownOpen && (
          <div className="fixed bottom-16 left-4 w-[180px] rounded-[24px] shadow-elev-3-popover border border-tertiary-background bg-white p-2 z-[99999]">
            <div className="px-3 py-2">
              <div className="text-ui text-light-grey">App version 2.13.19</div>
            </div>
            
            <button
              onClick={() => {
                onSettings?.();
                setIsDropdownOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2 text-ui text-primary-text hover:bg-tertiary-background rounded-[36px] transition-colors"
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
            
            <SignOutButton isLoading={isLoading} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-auto relative" ref={dropdownRef}>
      <div 
        className="flex items-center space-x-3 transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer rounded-lg p-2 -m-2"
        onClick={toggleDropdown}
      >
        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] shadow-sm">
          <span className="text-white text-ui">{initials}</span>
        </div>
        <div className="flex-1 min-w-0 transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]">
          <p
            className="
              text-ui tracking-[-0.02em]
              text-primary-text truncate
            "
          >
            {user.name}
          </p>
        </div>
      </div>

      {/* Dropdown menu for expanded state */}
      {isDropdownOpen && (
        <div
          className="fixed bottom-16 left-6 w-[190px] bg-white border border-tertiary-background rounded-[24px] shadow-elev-3-popover p-2 z-[99999]"
        >
          <div className="px-3 py-2">
            <div className="text-ui text-light-grey">App version 2.13.19</div>
          </div>
          
          <button
            onClick={() => {
              onSettings?.();
              setIsDropdownOpen(false);
            }}
            className="w-full flex items-center gap-3 px-3 py-2 text-ui text-primary-text hover:bg-tertiary-background rounded-[36px] transition-colors"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
          
          <SignOutButton isLoading={isLoading} />
        </div>
      )}
    </div>
  );
};
